import { useBunja } from 'bunja/react';
import { useAtomValue } from 'jotai';

import { Link, usePathname } from '@flipster/i18n/routing';
import CaretDownIcon from '@flipster/react/icon/generated/CaretDownIcon';
import { css } from '@flipster/styled-system/css';
import { hstack } from '@flipster/styled-system/patterns';

import { promotionBunja } from '@web/app/_layout/navigation/promotion';

import { NavItem } from './nav-item';
import { menuListCssRaw, menuSelectedCssRaw, tradFiGradientCssRaw, visibleHiddenDesktopDownCssRaw } from './shared/css';
import { Dropdown } from './shared/dropdown';
import RedDot from './shared/red-dot';
import { GroupNav, MenuNav, Navigations } from './shared/state';
import { tradFiBannerBunja } from './shared/tradfi-banner/state';
import { useNav } from './shared/useNav';

export const GnbList = () => {
  const { navigations, onClickLink } = useNav();
  const { promotionAtom } = useBunja(promotionBunja);
  const promotion = useAtomValue(promotionAtom);
  const pathname = usePathname();
  // Trade 와 TradFi 는 둘 다 /trade 경로라서 instrument 태그로만 구분 가능 (data-driven, alert-banner 와 동일 atom)
  const isTradFiTradePage = useAtomValue(useBunja(tradFiBannerBunja).isTradFiTradePageAtom);

  return (
    <ul className={css(hstack.raw({ flex: 1, gap: 8 }), visibleHiddenDesktopDownCssRaw)}>
      {navigations.map((item) => {
        if (item.isHide) return null;
        const isActive = isNavItemActive(item, pathname, isTradFiTradePage);
        return (
          <li key={`gnb-${item.label}-${item.type}`}>
            {item.type === 'menu' && <MenuCase item={item} isActive={isActive} />}
            {item.type === 'group' && <GroupCase item={item} isActive={isActive} />}
            {item.type === 'link' && (
              <Link
                href={item.url}
                key={item.url}
                onClick={() => onClickLink(item.label)}
                className={css(menuCssRaw, isActive && menuSelectedCssRaw)}
              >
                <RedDot
                  css={{ smallDesktopDown: { _after: { visibility: 'hidden !important' } } }}
                  redDotKey={item.redDotKey}
                >
                  {item.animatedGradient && !isActive ? (
                    <span className={css(tradFiGradientCssRaw)}>{item.label}</span>
                  ) : (
                    item.label
                  )}
                </RedDot>
              </Link>
            )}
          </li>
        );
      })}
      {promotion && (
        <li>
          <Link
            href={promotion.url ?? '/'}
            key={promotion.text}
            onClick={() => onClickLink(promotion.text ?? '')}
            className={css(menuCssRaw, menuSelectedCssRaw)}
          >
            <img src={`${promotion.image?.url}?fm=webp&q=80`} alt={promotion.text ?? ''} width={24} height={24} />
            {promotion.text}
          </Link>
        </li>
      )}
    </ul>
  );
};

// 타입 추론 편하게 하기 위해서 따로 컴포넌트 만듬
const MenuCase = ({ item, isActive }: { item: MenuNav; isActive: boolean }) => {
  const { onClickLink } = useNav();

  return (
    <Dropdown
      position='left'
      trigger={
        <div className={css(menuCssRaw, isActive && menuSelectedCssRaw)}>
          {item.label}
          <CaretDownIcon size={12} className='dropdown-icon' />
        </div>
      }
      dropdown={
        <ul className={css(menuListCssRaw)}>
          {item.children
            .filter((child) => !child.isHide)
            .map((child) => (
              <li key={`gnb-${item.label}-${child.label}`}>
                <NavItem item={child} onClick={() => onClickLink(item.label, child.label)} />
              </li>
            ))}
        </ul>
      }
    />
  );
};

const GroupCase = ({ item, isActive }: { item: GroupNav; isActive: boolean }) => {
  const { onClickLink } = useNav();

  return (
    <Dropdown
      position='left'
      trigger={
        <div className={css(menuCssRaw, isActive && menuSelectedCssRaw)}>
          {item.label}
          <CaretDownIcon size={12} className='dropdown-icon' />
        </div>
      }
      dropdown={
        <>
          {item.children
            .filter((child) => child.children.length && !child.isHide)
            .map((group) => (
              <ul className={css(menuListCssRaw)} key={`gnb-${item.label}-${group.label}`} style={group.style}>
                <li className={css({ textStyle: 'Text/S/Strong', color: 'content.default.level-2', pt: 8, pb: 4 })}>
                  {group.label}
                </li>
                {group.children
                  .filter((child) => !child.isHide)
                  .map((child) => (
                    <li key={`gnb-${item.label}-${group.label}-${child.label}`}>
                      <NavItem item={child} onClick={() => onClickLink(item.label, child.label)} />
                    </li>
                  ))}
              </ul>
            ))}
        </>
      }
    />
  );
};

const firstSegment = (path: string) => path.split('?')[0].replace(/\/+$/, '').split('/')[1] ?? '';

const collectNavUrls = (item: Navigations): string[] => {
  switch (item.type) {
    case 'link':
      return [item.url];
    case 'menu':
      return item.children.map((child) => child.url);
    case 'group':
      return item.children.flatMap((child) => child.children.map((leaf) => leaf.url));
    default:
      item satisfies never;
      return [];
  }
};

const isNavItemActive = (item: Navigations, pathname: string, isTradFiTradePage: boolean): boolean => {
  const currentSegment = firstSegment(pathname);
  if (!currentSegment) return false;

  const itemSegments = collectNavUrls(item).map(firstSegment);
  if (!itemSegments.includes(currentSegment)) return false;

  // /trade 는 Trade·TradFi 두 항목이 공유하는 경로라, TradFi 페이지 여부로 한 쪽만 활성화한다.
  if (currentSegment === 'trade') {
    const isTradFiItem = item.type === 'link' && Boolean(item.animatedGradient);
    return isTradFiItem === isTradFiTradePage;
  }
  return true;
};

// h 40 은 버튼과 동일하게 해서 dropdown 위치 동일하게 유지
const menuCssRaw = hstack.raw({
  textStyle: 'Text/M/Strong',
  color: 'content.default.level-3',
  h: 40,
  px: 20,
  gap: 4,
  cursor: 'pointer',
  _hover: {
    color: 'content.accent.info',
  },
  '.dropdown:hover &': {
    color: 'content.accent.info',
  },
});
